import { useEffect } from 'react';
import useIdentity from '../useIdentity';
import { useProductSettings, type ProductSettings } from '@workspace/utils-react';
import { AuthProvider } from '../../../projects/src/providers/AuthProvider';
import { configureAuth } from '../../../projects/src/lib/auth-fetch';
import type { ReactNode } from 'react';

interface AuthBridgeProviderProps {
  instanceId?: string;
  children: ReactNode;
}

export function AuthBridgeProvider({ instanceId, children }: AuthBridgeProviderProps): React.ReactElement {
  const { getAccessToken, signin, loading, error, isAuthenticated, user } = useIdentity();
  const settings = useProductSettings<ProductSettings>();

  useEffect(() => {
    if (!isAuthenticated && !loading && !error) {
      signin();
    }
  }, [isAuthenticated, loading, error, signin]);

  useEffect(() => {
    if (error && (error as any)?.message === 'login_required' && settings?.workspaceUrl) {
      const message = {
        type: 'RequestReauthentication',
      };
      window.parent.postMessage(JSON.stringify(message), settings.workspaceUrl);
    }
  }, [error, settings?.workspaceUrl]);

  useEffect(() => {
    configureAuth({ getAccessToken, instanceId });
  }, [getAccessToken, instanceId]);

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading authentication...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Authentication Error: {error?.message}</h2>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <AuthProvider getAccessToken={getAccessToken} instanceId={instanceId} isProduction={true}>
        {children}
      </AuthProvider>
    );
  }

  return (
    <div className="unauthenticated-container">
      <p>Authentication required...</p>
    </div>
  );
}
